import {Component, inject, OnInit} from '@angular/core';
import { CommonModule } from '@angular/common';
import {ReactiveFormsModule, FormBuilder, FormControl, FormsModule, Validators} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';

import { MatCardModule } from '@angular/material/card';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { ButtonsComponent } from '../../shared/buttons/buttons.component';
import { AuthService } from '../../core/services/auth/auth.service';
import { ThemeService } from '../../core/services/theme/theme.service';
import { InputSwitchModule } from 'primeng/inputswitch';
import {MatButtonModule} from '@angular/material/button';
import {SwalService} from '../../core/services/alerts/swal.service';
import { TenantService } from '../../core/services/tenant.service';
import {TenantLogoService} from '../../core/services/TenantLogoService';

// Il campo OTP compare dopo l'invio dello step email/password, che marca già
// il FormGroupDirective come "submitted": senza questo matcher Material lo
// mostrerebbe subito in stato di errore (bordo rosso) anche se l'utente non
// l'ha ancora toccato. Qui l'errore si mostra solo dopo che l'utente lo tocca,
// coerentemente con il *ngIf="f.otp.touched" già usato per il mat-error testuale.
class OtpErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null): boolean {
    return !!(control && control.invalid && control.touched);
  }
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, FormsModule, RouterLink,
    MatCardModule, MatFormFieldModule, MatInputModule, MatIconModule,
    ButtonsComponent, MatProgressSpinnerModule, MatCheckboxModule,
    InputSwitchModule, MatButtonModule
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  hostname = window.location.hostname;
  tenant=  this.hostname.split('.')[0].toLowerCase()
  logoService = inject(TenantLogoService);
  logoPath = this.logoService.logoUrl;
  private fb = inject(FormBuilder);
  private auth = inject(AuthService);
  private router = inject(Router);
  private themeService = inject(ThemeService);
  private swalService = inject(SwalService);
  private tenantService = inject(TenantService);
  demoDaysLeft = this.tenantService.daysLeft;
  hidePassword = true;
  loading = false;
  error = '';
  otpStep = false;
  otpErrorStateMatcher = new OtpErrorStateMatcher();

  darkMode = this.themeService.darkMode;

  form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(6)]],
    remember: [true],
    otp: [''],
  });
  ngOnInit() {
    this.tenantService.load(this.tenant).subscribe();
    this.logoService.loadLogo().subscribe();
  }
  get f() {
    return this.form.controls;
  }

  setDarkMode(enabled: boolean) {
    this.themeService.setDarkMode(enabled);
  }

  submit() {
    if (this.loading) return;

    this.error = '';

    if (this.otpStep) {
      this.verifyOtp();
      return;
    }

    this.login();
  }

  goToResetPassword(): void {
    this.router.navigateByUrl('/reset-pwd');
  }

  public changeAccount() {
    this.otpStep = false;
    this.error = '';
    this.loading = false;

    this.form.controls.email.enable();
    this.form.controls.password.enable();
    this.form.controls.remember.enable();

    this.form.controls.otp.reset('');
    this.form.controls.otp.clearValidators();
    this.form.controls.otp.updateValueAndValidity();

    this.form.controls.password.reset('');
    this.hidePassword = true;
  }

  private login() {
    this.form.controls.otp.clearValidators();
    this.form.controls.otp.updateValueAndValidity();

    if (
      this.form.controls.email.invalid ||
      this.form.controls.password.invalid
    ) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;

    const { email, password, remember } = this.form.getRawValue();
    const trimmedEmail = email?.trim() ?? '';
    const trimmedPassword = password?.trim() ?? '';

    this.form.patchValue({
      email: trimmedEmail,
      password: trimmedPassword,
    });

    this.auth.login({
      email: trimmedEmail,
      password: trimmedPassword,
      remember: remember ?? false,
    }).subscribe({
      next: (res) => {
        this.loading = false;

        // CASO 1: login diretto con token
        if (res.token) {
          this.router.navigateByUrl('/dashboard');
          return;
        }

        // CASO 2: OTP inviato, niente token
        this.otpStep = true;

        this.form.controls.email.disable();
        this.form.controls.password.disable();
        this.form.controls.remember.disable();

        this.form.controls.otp.setValidators([Validators.required]);
        this.form.controls.otp.reset('');
        this.form.controls.otp.updateValueAndValidity();

        this.error = '';
      },
      error: (err) => {
        this.loading = false;
        this.error = 'Controlla email e password, poi riprova.';
        console.error('[login]', err);
      },
    });
  }

  private verifyOtp() {
    this.form.controls.otp.setValidators([Validators.required]);
    this.form.controls.otp.updateValueAndValidity();

    if (this.form.controls.otp.invalid) {
      this.form.controls.otp.markAsTouched();
      return;
    }

    this.loading = true;

    const { email, otp , remember } = this.form.getRawValue();
    const trimmedEmail = email?.trim() ?? '';
    const trimmedOtp = otp?.trim() ?? '';
    this.auth.verifyOtp({
      email: trimmedEmail,
      otp: trimmedOtp,
      remember: remember ?? false,
    }).subscribe({
      next: () => {
        this.loading = false;
        this.router.navigateByUrl('/dashboard');
      },
      error: (err: any) => {
        this.loading = false;
        this.error = 'Controlla il codice ricevuto e riprova.';
        console.error('[verifyOtp]', err);
      },
    });
  }
}
